import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2, ChevronRight, Layers } from "lucide-react";
import { VARIATION_SCHEMAS, SIZE_CHARTS } from "@/data/variationSchemas";

/**
 * VariationSchemaEditor — admin tab to override the per-category variation
 * schemas without a code change. Only shows fields that are relevant to each
 * category (based on the hardcoded default schema).
 *
 * A category's schema override is merged on top of the code default at runtime
 * by SchemasContext, so leaving a field blank / untouched keeps the default.
 */

const OPTIONAL_KEYS = ["sizes", "colours", "fabrics", "closures", "header", "bagType", "designStyle", "monogramFont", "sizeChart"];

const LIST_LABELS = {
  closures: "Closures",
  header: "Header styles",
  bagType: "Bag types",
  designStyle: "Design styles",
  monogramFont: "Monogram fonts",
};

const parseLines = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean);
const joinLines = (arr) => (arr || []).join("\n");

export const VariationSchemaEditor = ({ authAxios, categories }) => {
  const [overrides, setOverrides] = useState({});
  const [activeSlug, setActiveSlug] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const activeCats = useMemo(
    () => (categories || []).filter((c) => !c.is_deleted).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [categories]
  );

  const editableCats = useMemo(
    () => activeCats.filter((c) => VARIATION_SCHEMAS[c.slug]),
    [activeCats]
  );

  const filteredCats = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return editableCats;
    return editableCats.filter((c) => c.slug.includes(s) || (c.label || "").toLowerCase().includes(s));
  }, [editableCats, q]);

  const loadAll = async () => {
    try {
      const { data } = await authAxios().get("/admin/variation-schemas");
      setOverrides(data || {});
    } catch (e) {
      toast.error("Failed to load schemas");
    }
  };

  useEffect(() => {
    loadAll();
    if (!activeSlug && editableCats.length) setActiveSlug(editableCats[0].slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeSlug) return;
    const def = VARIATION_SCHEMAS[activeSlug] || {};
    const ov = overrides[activeSlug] || {};
    // Materialise the draft from override-if-present-else-default
    setDraft({
      sizes: ov.sizes || def.sizes || null,
      colours: ov.colours || def.colours || null,
      fabrics: ov.fabrics || def.fabrics || null,
      closures: ov.closures || def.closures || null,
      header: ov.header || def.header || null,
      bagType: ov.bagType || def.bagType || null,
      designStyle: ov.designStyle || def.designStyle || null,
      monogramFont: ov.monogramFont || def.monogramFont || null,
      sizeChart: ov.sizeChart || def.sizeChart || null,
    });
  }, [activeSlug, overrides]);

  if (!editableCats.length) {
    return <p className="text-forest/60">No editable categories found.</p>;
  }

  const def = VARIATION_SCHEMAS[activeSlug] || {};
  const hasKey = (k) => Object.prototype.hasOwnProperty.call(def, k);
  const isOverridden = !!overrides[activeSlug];

  const setDraftField = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async () => {
    if (!activeSlug || !draft) return;
    setSaving(true);
    try {
      // Only send fields that the default schema defines — keeps payloads clean
      const body = {};
      for (const k of OPTIONAL_KEYS) if (hasKey(k) && draft[k] != null) body[k] = draft[k];
      const { data } = await authAxios().put(`/admin/variation-schemas/${activeSlug}`, body);
      setOverrides((m) => ({ ...m, [activeSlug]: data }));
      toast.success(`Saved variations for ${activeSlug}`);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!activeSlug) return;
    if (!window.confirm(`Reset ${activeSlug} to code defaults? This removes your override.`)) return;
    try {
      await authAxios().delete(`/admin/variation-schemas/${activeSlug}`);
      const next = { ...overrides };
      delete next[activeSlug];
      setOverrides(next);
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-6 min-h-[600px]" data-testid="admin-variation-editor">
      {/* Category list */}
      <aside className="col-span-12 md:col-span-4 lg:col-span-3 border border-hairline bg-white flex flex-col">
        <div className="px-4 py-3 border-b border-hairline bg-cream flex items-center gap-2">
          <Layers size={14} strokeWidth={1.6} className="text-champagne" />
          <span className="overline text-forest/70">Categories</span>
        </div>
        <input
          data-testid="ve-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          className="px-4 py-2 border-b border-hairline bg-transparent text-sm text-forest focus:outline-none"
        />
        <div className="flex-1 overflow-y-auto">
          {filteredCats.map((c) => {
            const on = c.slug === activeSlug;
            const ov = !!overrides[c.slug];
            return (
              <button
                key={c.slug}
                data-testid={`ve-cat-${c.slug}`}
                onClick={() => setActiveSlug(c.slug)}
                className={`w-full text-left px-4 py-3 border-b border-hairline flex items-center justify-between gap-2 ${
                  on ? "bg-bone" : "hover:bg-cream"
                }`}
              >
                <div className="min-w-0">
                  <div className="font-display text-forest text-base leading-none truncate">{c.label}</div>
                  <div className="overline text-forest/40 text-[10px] mt-1">{c.slug}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {ov && <span data-testid={`ve-cat-override-${c.slug}`} className="w-2 h-2 rounded-full bg-champagne" title="Overridden" />}
                  <ChevronRight size={13} strokeWidth={1.4} className={on ? "text-forest" : "text-forest/40"} />
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Editor */}
      <section className="col-span-12 md:col-span-8 lg:col-span-9 border border-hairline bg-white p-6 overflow-y-auto">
        {!draft ? (
          <p className="text-forest/60">Select a category to edit.</p>
        ) : (
          <>
            <header className="flex items-center justify-between gap-4 mb-6 pb-4 border-b border-hairline">
              <div>
                <div className="overline text-gold">§ Variations</div>
                <div className="font-display text-forest text-3xl leading-none">
                  {activeCats.find((c) => c.slug === activeSlug)?.label || activeSlug}
                </div>
                <div className="overline text-forest/50 text-[10px] mt-1">
                  {isOverridden ? "Custom override active" : "Using code defaults"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isOverridden && (
                  <button
                    onClick={reset}
                    data-testid="ve-reset"
                    className="inline-flex items-center gap-2 overline rounded-full px-3 py-2 border border-hairline text-forest/70 hover:bg-cream"
                  >
                    <RotateCcw size={12} strokeWidth={1.6} /> Reset
                  </button>
                )}
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="ve-save"
                  className="inline-flex items-center gap-2 overline rounded-full px-4 py-2 bg-forest text-white disabled:opacity-40"
                >
                  <Save size={12} strokeWidth={1.6} /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </header>

            <div className="space-y-8">
              {hasKey("sizes") && (
                <SizesEditor value={draft.sizes} onChange={(sizes) => setDraftField({ sizes })} />
              )}
              {hasKey("colours") && (
                <ColoursEditor value={draft.colours} onChange={(colours) => setDraftField({ colours })} />
              )}
              {hasKey("fabrics") && (
                <FabricsEditor value={draft.fabrics} onChange={(fabrics) => setDraftField({ fabrics })} />
              )}
              {["closures", "header", "bagType", "designStyle", "monogramFont"].map((k) => hasKey(k) && (
                <ChipListEditor
                  key={k}
                  fieldKey={k}
                  label={LIST_LABELS[k]}
                  value={draft[k]}
                  onChange={(v) => setDraftField({ [k]: v })}
                />
              ))}
              {hasKey("sizeChart") && (
                <SizeChartPicker value={draft.sizeChart} onChange={(sizeChart) => setDraftField({ sizeChart })} />
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

/* ---- sub-editors ---- */

const Section = ({ title, hint, children }) => (
  <div className="border border-hairline rounded-lg p-4 bg-cream/40">
    <div className="flex items-baseline justify-between gap-3 mb-3">
      <div className="font-display text-forest text-lg leading-none">{title}</div>
      {hint && <div className="overline text-forest/40 text-[10px]">{hint}</div>}
    </div>
    {children}
  </div>
);

const CheckRow = ({ label, checked, onChange, testId }) => (
  <label className="inline-flex items-center gap-2 text-forest text-sm cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="accent-forest" />
    {label}
  </label>
);

const SizesEditor = ({ value, onChange }) => {
  const isRegional = !!value?.regions;
  const [mode, setMode] = useState(isRegional ? "regional" : (value?.list ? "list" : "list"));

  const setList = (text) => onChange({ ...(value || {}), list: parseLines(text), regions: undefined });
  const setRegions = (region, text) =>
    onChange({ ...(value || {}), regions: { ...(value?.regions || {}), [region]: parseLines(text) }, list: undefined });
  const setAllowCustom = (b) => onChange({ ...(value || {}), allowCustom: b });

  const switchMode = (m) => {
    setMode(m);
    if (m === "list") onChange({ list: value?.list || [], allowCustom: !!value?.allowCustom });
    else onChange({ regions: value?.regions || { US: [], UK: [], IN: [] }, allowCustom: !!value?.allowCustom });
  };

  return (
    <Section title="Sizes" hint="One size per line">
      <div className="flex items-center gap-2 mb-3">
        <button
          data-testid="ve-sizes-mode-list"
          onClick={() => switchMode("list")}
          className={`overline text-[10px] px-3 py-1 rounded-full border ${mode === "list" ? "bg-forest text-white border-forest" : "text-forest border-hairline"}`}
        >
          Single list
        </button>
        <button
          data-testid="ve-sizes-mode-regional"
          onClick={() => switchMode("regional")}
          className={`overline text-[10px] px-3 py-1 rounded-full border ${mode === "regional" ? "bg-forest text-white border-forest" : "text-forest border-hairline"}`}
        >
          Regional (US / UK / IN)
        </button>
      </div>
      {mode === "list" ? (
        <textarea
          data-testid="ve-sizes-list"
          rows={5}
          value={joinLines(value?.list)}
          onChange={(e) => setList(e.target.value)}
          placeholder={"Queen\nKing\n72&quot; × 84&quot;"}
          className="w-full bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest font-mono"
        />
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {["US", "UK", "IN"].map((r) => (
            <div key={r}>
              <div className="overline text-forest/60 mb-1 text-[10px]">{r}</div>
              <textarea
                data-testid={`ve-sizes-region-${r}`}
                rows={6}
                value={joinLines(value?.regions?.[r])}
                onChange={(e) => setRegions(r, e.target.value)}
                className="w-full bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest font-mono"
              />
            </div>
          ))}
        </div>
      )}
      <div className="mt-3">
        <CheckRow
          label="Allow custom size input on the product page"
          checked={value?.allowCustom}
          onChange={setAllowCustom}
          testId="ve-sizes-allowcustom"
        />
      </div>
    </Section>
  );
};

const ColoursEditor = ({ value, onChange }) => {
  const options = value?.options || [];
  const setOptions = (next) => onChange({ ...(value || {}), options: next });
  const setAllowCustom = (b) => onChange({ ...(value || {}), allowCustom: b });

  const update = (i, patch) => setOptions(options.map((o, k) => (k === i ? { ...o, ...patch } : o)));
  const remove = (i) => setOptions(options.filter((_, k) => k !== i));
  const add = () => setOptions([...options, { name: "New", hex: "#CCCCCC" }]);

  return (
    <Section title="Colours" hint="Name + hex; drag not required">
      <div className="space-y-2">
        {options.map((o, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input
              data-testid={`ve-colour-name-${i}`}
              value={o.name}
              onChange={(e) => update(i, { name: e.target.value })}
              className="col-span-6 bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
              placeholder="Ivory"
            />
            <input
              data-testid={`ve-colour-hex-${i}`}
              value={o.hex}
              onChange={(e) => update(i, { hex: e.target.value })}
              className="col-span-3 bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest font-mono uppercase"
              placeholder="#F5EFE0"
            />
            <input
              type="color"
              value={o.hex && /^#[0-9A-Fa-f]{6}$/.test(o.hex) ? o.hex : "#cccccc"}
              onChange={(e) => update(i, { hex: e.target.value })}
              className="col-span-2 h-9 border border-hairline rounded"
              aria-label="Pick colour"
            />
            <button
              data-testid={`ve-colour-remove-${i}`}
              onClick={() => remove(i)}
              className="col-span-1 text-forest/50 hover:text-forest inline-flex justify-center"
              aria-label="Remove"
            >
              <Trash2 size={14} strokeWidth={1.4} />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={add}
        data-testid="ve-colour-add"
        className="mt-3 inline-flex items-center gap-2 overline text-[10px] px-3 py-2 border border-hairline text-forest rounded-full hover:bg-white"
      >
        <Plus size={12} strokeWidth={1.6} /> Add colour
      </button>
      <div className="mt-3">
        <CheckRow
          label="Allow custom colour input on the product page"
          checked={value?.allowCustom}
          onChange={setAllowCustom}
          testId="ve-colour-allowcustom"
        />
      </div>
    </Section>
  );
};

const FabricsEditor = ({ value, onChange }) => (
  <Section title="Fabrics" hint="One fabric per line">
    <textarea
      data-testid="ve-fabrics"
      rows={5}
      value={joinLines(value?.options)}
      onChange={(e) => onChange({ ...(value || {}), options: parseLines(e.target.value) })}
      className="w-full bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest font-mono"
    />
    <div className="mt-3">
      <CheckRow
        label="Allow custom fabric input on the product page"
        checked={value?.allowCustom}
        onChange={(b) => onChange({ ...(value || {}), allowCustom: b })}
        testId="ve-fabric-allowcustom"
      />
    </div>
  </Section>
);

const ChipListEditor = ({ fieldKey, label, value, onChange }) => {
  // closures is a plain array; the rest live under `.options`
  const isPlain = fieldKey === "closures";
  const items = isPlain ? (value || []) : (value?.options || []);
  const setItems = (arr) => onChange(isPlain ? arr : { ...(value || {}), options: arr });
  return (
    <Section title={label} hint="One option per line">
      <textarea
        data-testid={`ve-${fieldKey}`}
        rows={4}
        value={joinLines(items)}
        onChange={(e) => setItems(parseLines(e.target.value))}
        className="w-full bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest font-mono"
      />
    </Section>
  );
};

const SizeChartPicker = ({ value, onChange }) => {
  const keys = ["", ...Object.keys(SIZE_CHARTS)];
  return (
    <Section title="Size chart" hint="Pick which baked-in chart to link">
      <select
        data-testid="ve-sizechart"
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
      >
        {keys.map((k) => (
          <option key={k || "none"} value={k}>{k ? `${k} — ${SIZE_CHARTS[k].title}` : "None"}</option>
        ))}
      </select>
    </Section>
  );
};
